/**
 * Helpers for normalising event-shape fields that the backend has refactored
 * from scalars into nested objects (e.g. `venue` and `location`), and for
 * formatting the optional enrichment fields (price, capacity, audience, etc.)
 * surfaced by Refactor 1.
 *
 * All helpers gracefully handle missing values — they return `''` / `null` /
 * empty arrays rather than throwing, so call sites can short-circuit with a
 * simple truthy check before rendering.
 */

import type {
  EventCoordinates,
  EventLocation,
  EventSpeaker,
  EventVenue,
} from '../types/api';
import { formatEventLocalDateTime } from './eventDateTime';

/**
 * Return a renderable venue label from either the legacy string form or the
 * structured Venue object now returned by some endpoints. Returns `''` when
 * no usable label is present.
 */
export function getVenueName(venue: unknown): string {
  if (venue == null) return '';
  if (typeof venue === 'string') return venue;
  if (typeof venue === 'object') {
    const v = venue as EventVenue;
    if (typeof v.name === 'string' && v.name.trim()) return v.name;
    if (typeof v.streetAddress === 'string' && v.streetAddress.trim()) return v.streetAddress;
  }
  return '';
}

/** Return the venue's street address if available, else `''`. */
export function getVenueStreetAddress(venue: unknown): string {
  if (venue == null || typeof venue === 'string') return '';
  const v = venue as EventVenue;
  return typeof v.streetAddress === 'string' ? v.streetAddress.trim() : '';
}

/**
 * Return the most specific human-readable address for a venue: prefer the
 * geocoded `formattedAddress` ("705 Pike St, Seattle, WA 98101, US"), fall
 * back to the bare `streetAddress`. Empty string when neither is present.
 */
export function getVenueFormattedAddress(venue: unknown): string {
  if (venue == null || typeof venue === 'string') return '';
  const v = venue as EventVenue;
  if (typeof v.formattedAddress === 'string' && v.formattedAddress.trim()) {
    return v.formattedAddress.trim();
  }
  if (v.location && typeof v.location.formattedAddress === 'string' && v.location.formattedAddress.trim()) {
    return v.location.formattedAddress.trim();
  }
  if (typeof v.streetAddress === 'string' && v.streetAddress.trim()) {
    return v.streetAddress.trim();
  }
  return '';
}

/**
 * Return the venue's geocoding lifecycle status (`'pending' | 'success' | ...`).
 * Returns `''` when no venue is present.
 */
export function getVenueGeocodeStatus(venue: unknown): string {
  if (venue == null || typeof venue === 'string') return '';
  const v = venue as EventVenue;
  if (typeof v.geocodeStatus === 'string' && v.geocodeStatus.trim()) return v.geocodeStatus.trim();
  if (v.location && typeof v.location.geocodeStatus === 'string' && v.location.geocodeStatus.trim()) {
    return v.location.geocodeStatus.trim();
  }
  return '';
}

/**
 * `skipped` and `failed` are terminal in the geocoder, so promising the address
 * is "being verified" would be untrue.
 */
export function getVenueAddressNotice(venue: unknown): string {
  const status = getVenueGeocodeStatus(venue);
  return status === 'skipped' || status === 'failed'
    ? 'Exact address not verified'
    : 'Address being verified';
}

/** True when the venue (or its parent location) has usable lat/long. */
export function hasVenueCoordinates(venue: unknown): boolean {
  if (venue == null || typeof venue === 'string') return false;
  const v = venue as EventVenue;
  if (typeof v.latitude === 'number' && typeof v.longitude === 'number') return true;
  if (v.location && typeof v.location.latitude === 'number' && typeof v.location.longitude === 'number') {
    return true;
  }
  return false;
}

/**
 * True when the city-level location block would only repeat information already
 * shown inside the venue's `formattedAddress`. Used by detail views to avoid
 * displaying both "4 Pennsylvania Plaza, New York, NY 10001, US" and a separate
 * "New York, NY, US" row right beneath it.
 */
export function isLocationRedundantWithVenue(
  event:
    | { venue?: string | EventVenue | null; location?: EventLocation | null; city?: unknown; state?: unknown; country?: unknown }
    | null
    | undefined,
): boolean {
  if (!event) return false;
  const venueAddress = getVenueFormattedAddress(event.venue ?? null);
  if (!venueAddress) return false;
  const locLabel = getLocationLabel({
    city: event.city,
    state: event.state,
    country: event.country,
    location: event.location ?? undefined,
  });
  if (!locLabel) return true;
  const haystack = venueAddress.toLowerCase();
  const parts = locLabel
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  if (!parts.length) return true;
  return parts.every((p) => haystack.includes(p));
}

/**
 * Combine city / state / country into a "Seattle, WA, USA" style label.
 * Tolerant of either flat string fields on the event or a structured
 * `location` object.
 */
export function getLocationLabel(
  source:
    | { city?: unknown; state?: unknown; country?: unknown; location?: EventLocation }
    | null
    | undefined,
): string {
  if (!source) return '';
  const loc: any = source.location ?? source;
  const parts = [loc.city, loc.state, loc.country]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter(Boolean);
  return parts.join(', ');
}

/** Extract `{ lat, lng }` if the structured location has both. */
export function getCoordinates(location: EventLocation | null | undefined):
  | { latitude: number; longitude: number }
  | null {
  if (!location) return null;
  if (typeof location.latitude !== 'number' || typeof location.longitude !== 'number') return null;
  return { latitude: location.latitude, longitude: location.longitude };
}

function normalizeLocationText(value: unknown): string {
  return value == null
    ? ''
    : String(value)
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('en-US')
      .replace(/[.,'’\-_/\\()[\]{}:;]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
}

function hasLocationPrecision(location: EventLocation | null | undefined, fallbackCity?: unknown): boolean {
  const city = normalizeLocationText(location?.city ?? fallbackCity);
  const postalCode = normalizeLocationText(location?.postalCode);
  const formattedAddress = normalizeLocationText(location?.formattedAddress);
  if (!formattedAddress) return false;
  if (city) {
    const locality = normalizeLocationText(location?.formattedAddress?.split(',')[0]);
    return formattedAddress.includes(city) || (locality.length >= 2 && city.includes(locality));
  }
  return Boolean(postalCode && formattedAddress.includes(postalCode));
}

/**
 * Resolve the best available coordinates for an event from the standardized
 * envelope. Preference order:
 *
 *   1. Top-level `event.coordinates` (already venue-preferred by the server).
 *   2. `venue.latitude/longitude` (or `venue.location.latitude/longitude`).
 *   3. `event.location.latitude/longitude`.
 *
 * The returned object always includes `source` so callers can show "street"
 * vs "city" precision in the UI. Returns `null` when no coordinates exist.
 */
export function getEventCoordinates(
  event:
    | {
        coordinates?: EventCoordinates | null;
        venue?: string | EventVenue | null;
        location?: EventLocation | null;
        city?: unknown;
      }
    | null
    | undefined,
): EventCoordinates | null {
  if (!event) return null;
  const top = event.coordinates;
  if (
    top &&
    typeof top.latitude === 'number' &&
    typeof top.longitude === 'number'
  ) {
    const source = top.source === 'location' ? 'location' : 'venue';
    if (top.validated === true || source === 'venue' || hasLocationPrecision(event.location, event.city)) {
      return {
        latitude: top.latitude,
        longitude: top.longitude,
        source,
        ...(top.validated === true ? { validated: true } : {}),
        ...(top.inferredFrom ? { inferredFrom: top.inferredFrom } : {}),
      };
    }
  }
  if (event.venue && typeof event.venue === 'object') {
    const v = event.venue as EventVenue;
    if (typeof v.latitude === 'number' && typeof v.longitude === 'number') {
      return { latitude: v.latitude, longitude: v.longitude, source: 'venue' };
    }
    const vc = hasLocationPrecision(v.location) ? getCoordinates(v.location ?? undefined) : null;
    if (vc) return { ...vc, source: 'venue' };
  }
  const lc = hasLocationPrecision(event.location, event.city)
    ? getCoordinates(event.location ?? undefined)
    : null;
  if (lc) return { ...lc, source: 'location' };
  return null;
}

export function getCoordinatePrecisionLabel(coordinates: EventCoordinates): string {
  if (coordinates.inferredFrom === 'university-primary-location') {
    return 'campus-level (University host)';
  }
  if (coordinates.inferredFrom === 'city-entity') return 'city-level (City entity)';
  return coordinates.source === 'venue'
    ? 'street-level (venue)'
    : 'city-level (location)';
}

// ---------------------------------------------------------------------------
// Enrichment formatters
// ---------------------------------------------------------------------------

/** Format a price + currency into "$25.00" / "USD 25.00" / "Free". */
export function formatPrice(price?: number | null, currency?: string | null, isFree?: boolean | null): string {
  if (isFree === true) return 'Free';
  if (typeof price !== 'number' || Number.isNaN(price)) return '';
  if (price === 0) return 'Free';
  const cur = (currency || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(price);
  } catch {
    return `${cur} ${price.toFixed(2)}`;
  }
}

/** "1,200 seats" or "1,200 seats (340 remaining)". Empty string if no capacity. */
export function formatCapacity(capacity?: number | null, ticketsRemaining?: number | null): string {
  if (typeof capacity !== 'number' || capacity <= 0) return '';
  const base = `${capacity.toLocaleString()} seat${capacity === 1 ? '' : 's'}`;
  if (typeof ticketsRemaining === 'number' && ticketsRemaining >= 0) {
    return `${base} (${ticketsRemaining.toLocaleString()} remaining)`;
  }
  return base;
}

/** Capitalise audience level for display. */
export function formatAudienceLevel(level?: string | null): string {
  if (!level || typeof level !== 'string') return '';
  const trimmed = level.trim();
  if (!trimmed) return '';
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

/**
 * Format a registration deadline as "Apr 30, 2026" (date) or
 * "Apr 30, 2026 at 5:00 PM" (with time) in the user's locale.
 */
export function formatDeadline(deadline?: string | null): string {
  return formatEventLocalDateTime(deadline);
}

/** Compact a speaker entry to "Name — Title, Company" / "Name (keynote)". */
export function formatSpeaker(speaker: EventSpeaker | null | undefined): string {
  if (!speaker || typeof speaker !== 'object') return '';
  const name = typeof speaker.name === 'string' ? speaker.name.trim() : '';
  if (!name) return '';
  const bits: string[] = [];
  if (speaker.title) bits.push(speaker.title);
  if (speaker.company) bits.push(speaker.company);
  const detail = bits.length > 0 ? ` — ${bits.join(', ')}` : '';
  const keynote = speaker.keynote ? ' (keynote)' : '';
  return `${name}${detail}${keynote}`;
}

/** Filter to only non-empty strings, dedupe, and trim each. */
export function cleanStringArray(arr: unknown): string[] {
  if (!Array.isArray(arr)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of arr) {
    if (typeof v !== 'string') continue;
    const t = v.trim();
    if (!t || seen.has(t.toLowerCase())) continue;
    seen.add(t.toLowerCase());
    out.push(t);
  }
  return out;
}
